import { Subhead } from "../components/SettingsLayout";
import { FileIcon, XIcon } from "@primer/octicons-react";
import { rp } from "../api/client";
import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useCodeGraph, useKnowledge } from "../api/hooks";
import type { CodeGraph as CodeGraphData, KnowledgeDoc } from "../api/types";
import { Empty, ErrorMessage, Loading, RefPill } from "../components/common";
import { short } from "../components/format";
import { GraphCanvas, type GEdge, type GNode } from "../components/GraphCanvas";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Select } from "../components/ui/select";

const LANG_COLORS = ["var(--accent)", "var(--success)", "var(--merged)", "var(--severe)", "var(--warning)", "var(--danger)", "var(--fg-muted)"];
const langColor = (langs: string[], lang: string) => LANG_COLORS[Math.max(langs.indexOf(lang), 0) % LANG_COLORS.length];
const base = (p: string) => p.slice(p.lastIndexOf("/") + 1);
const covers = (n: KnowledgeDoc, path: string) => n.paths.some((p) => path === p || path.startsWith(`${p}/`));

/** /:repo/graph — files linked by the identifiers they use, around one file or under one directory. */
export function CodeGraph() {
  const { repo = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const focus = params.get("focus") ?? "";
  const dir = params.get("paths") ?? "";
  const ref = params.get("ref") ?? "";
  const limit = Number(params.get("limit")) || 60;
  const selected = params.get("select");
  const q = useCodeGraph(repo, { commit: ref || undefined, focus, paths: dir, limit });
  const kg = useKnowledge(repo);

  const update = (next: Record<string, string | null>) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        for (const [k, v] of Object.entries(next)) v ? n.set(k, v) : n.delete(k);
        return n;
      },
      { replace: true },
    );

  return (
    <>
      <Subhead
        title={
          <>
            Graph
            {ref && <RefPill>{short(ref)}</RefPill>}
            {(focus || dir) && (
              <Badge>
                <code>{focus || dir}</code>
                <button
                  type="button"
                  aria-label="Show the whole repository"
                  className="ml-1 inline-flex"
                  onClick={() => update({ focus: null, paths: null, select: null })}
                >
                  <XIcon size={12} />
                </button>
              </Badge>
            )}
          </>
        }
        actions={
          <Select
            size="sm"
            prefix="Files:"
            aria-label="Most files to draw"
            value={String(limit)}
            onValueChange={(v) => update({ limit: v === "60" ? null : v })}
            options={["30", "60", "120", "200"].map((v) => ({ value: v, label: v }))}
          />
        }
      >
        An arrow from one file to another means the first uses identifiers the second defines.{" "}
        {focus ? "Showing this file and the files it is linked with." : "Showing the most referenced files."}
      </Subhead>
      {q.isLoading ? (
        <Card>
          <Loading rows={8} />
        </Card>
      ) : q.error ? (
        <ErrorMessage error={q.error} />
      ) : q.data && q.data.nodes.length === 0 ? (
        <Card>
          <Empty>
            {focus ? `No links found for ${focus}. It may not be a source file, or nothing else uses it.` : "Nothing to draw here: no indexed source files."}
          </Empty>
        </Card>
      ) : q.data ? (
        <GraphBody
          repo={repo}
          ref_={ref || "main"}
          data={q.data}
          selected={selected}
          kg={kg.data?.docs ?? []}
          onSelect={(id) => update({ select: id })}
          onFocus={(path) => update({ focus: path, paths: null, select: null })}
        />
      ) : null}
    </>
  );
}

function GraphBody({
  repo,
  ref_,
  data,
  selected,
  kg,
  onSelect,
  onFocus,
}: {
  repo: string;
  ref_: string;
  data: CodeGraphData;
  selected: string | null;
  kg: KnowledgeDoc[];
  onSelect: (id: string | null) => void;
  onFocus: (path: string) => void;
}) {
  const langs = useMemo(() => [...new Set(data.nodes.map((n) => n.lang))].sort(), [data.nodes]);
  const maxRank = Math.max(...data.nodes.map((n) => n.rank), 1e-9);
  const nodes: GNode[] = useMemo(
    () =>
      data.nodes.map((n) => ({
        id: n.path,
        label: base(n.path),
        title: n.path,
        color: langColor(langs, n.lang),
        r: 5 + 9 * Math.sqrt(n.rank / maxRank) + (n.focus ? 3 : 0),
      })),
    [data.nodes, langs, maxRank],
  );
  const edges: GEdge[] = useMemo(
    () => data.edges.map((e) => ({ id: `${e.from}>${e.to}`, from: e.from, to: e.to, weight: e.weight, label: e.names.join(", ") })),
    [data.edges],
  );
  const sel = data.nodes.find((n) => n.path === selected);
  const uses = data.edges.filter((e) => e.from === selected);
  const usedBy = data.edges.filter((e) => e.to === selected);
  const notes = sel ? kg.filter((n) => covers(n, sel.path)) : [];
  return (
    <Card className="grid lg:grid-cols-[1fr_320px]">
      <div className="min-w-0">
        <GraphCanvas nodes={nodes} edges={edges} selected={selected} onSelect={onSelect} directed className="h-[560px] rounded-none border-0 max-md:h-[420px]" />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t px-4 py-2 text-xs text-muted-foreground">
          {langs.map((l) => (
            <span key={l} className="inline-flex items-center gap-1.5">
              <span className="size-2.5 rounded-full" style={{ background: langColor(langs, l) }} /> {l}
            </span>
          ))}
          <span className="ml-auto">
            {data.nodes.length} of {data.total} files · {data.edges.length} links · drag to pan, ⌘/Ctrl + scroll to zoom
          </span>
        </div>
        {data.index_truncated && (
          <p className="border-t px-4 py-2 text-xs text-warning">This repository is larger than the code index, so some files are missing from the graph.</p>
        )}
      </div>
      <div className="min-w-0 border-t lg:border-l lg:border-t-0">
        <CardHeader>{sel ? <span className="truncate font-mono text-xs">{sel.path}</span> : "Select a file"}</CardHeader>
        <CardContent className="text-sm">
          {!sel ? (
            <p className="text-muted-foreground">Click a file to see what it uses and what uses it. Bigger circles are referenced by more files.</p>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge>{sel.lang}</Badge>
                <span className="text-muted-foreground">{sel.symbols} definitions</span>
              </div>
              <div className="flex gap-2">
                <Button asChild size="sm">
                  <Link to={`${rp(repo)}/src/${ref_}/${sel.path}`}>
                    <FileIcon /> Open file
                  </Link>
                </Button>
                {!sel.focus && (
                  <Button size="sm" onClick={() => onFocus(sel.path)}>
                    Focus here
                  </Button>
                )}
              </div>
              <Links title="Uses" items={uses.map((e) => ({ path: e.to, names: e.names }))} onPick={onSelect} />
              <Links title="Used by" items={usedBy.map((e) => ({ path: e.from, names: e.names }))} onPick={onSelect} />
              {notes.length > 0 && (
                <div>
                  <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Knowledge graph</h3>
                  <ul className="flex flex-col gap-1">
                    {notes.map((n) => (
                      <li key={n.id}>
                        <Link to={`${rp(repo)}/docs?view=docs&select=${encodeURIComponent(n.id)}`}>{n.title}</Link>{" "}
                        <span className="text-xs text-muted-foreground">{n.type}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </div>
    </Card>
  );
}

function Links({ title, items, onPick }: { title: string; items: { path: string; names: string[] }[]; onPick: (path: string) => void }) {
  if (items.length === 0) return null;
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">{title}</h3>
      <ul className="flex flex-col gap-1.5">
        {items.map((i) => (
          <li key={i.path} className="min-w-0">
            <button type="button" className="block max-w-full truncate text-left text-info hover:underline" title={i.path} onClick={() => onPick(i.path)}>
              {i.path}
            </button>
            <span className="block truncate font-mono text-xs text-muted-foreground">{i.names.join(", ")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
